import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { FileWarning, Upload, Video, MapPin, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const INCIDENT_TYPES = [
  "Physical Abuse",
  "Severe Neglect",
  "Animal Fighting",
  "Abandonment",
  "Hoarding",
  "Illegal Breeding",
  "Other",
];

const EVIDENCE_IMG =
  "https://images.unsplash.com/photo-1675430428387-376cabac44dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxwb2xpY2UlMjBjYXIlMjBsaWdodHMlMjBibHVyfGVufDB8fHx8MTc3NjYzMzA5MXww&ixlib=rb-4.1.0&q=85";

export default function ReportAbuse() {
  const [form, setForm] = useState({
    incident_type: "Physical Abuse",
    incident_description: "",
    location_text: "",
    city: "",
    state: "",
    reporter_name: "",
    reporter_email: "",
    reporter_phone: "",
  });
  const [video, setVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitted, setSubmitted] = useState(null);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.incident_description.trim()) {
      toast.error("Please describe the incident.");
      return;
    }
    if (!form.location_text.trim()) {
      toast.error("Please provide the incident location.");
      return;
    }
    setUploading(true);
    setProgress(5);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });
      if (video) fd.append("video", video);

      const { data } = await api.post("/reports", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const p = Math.min(98, Math.round((evt.loaded / evt.total) * 100));
            setProgress(p);
          }
        },
      });
      setProgress(100);
      setSubmitted(data);
      toast.success("Report submitted. Thank you for taking action.");
    } catch (err) {
      toast.error("Failed to submit report. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="report-success">
        <div className="border border-zinc-900 p-10 bg-white">
          <div className="w-14 h-14 bg-zinc-900 text-white flex items-center justify-center border border-red-600">
            <CheckCircle2 size={28} />
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-6">
            Report received.
          </h1>
          <p className="text-zinc-700 mt-4 leading-relaxed">
            Your report has been logged with reference ID{" "}
            <span className="font-mono font-bold">{submitted.id.slice(0, 8)}</span>.
            In a real emergency, call 911 or your local animal control.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link to="/nearby" className="btn-primary justify-center" data-testid="report-success-nearby">
              <MapPin size={14} /> Connect to Nearest Station
            </Link>
            <Link to="/" className="btn-outline justify-center" data-testid="report-success-home">
              Back to Registry
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="report-page">
      <SEO
        title="Report Animal Abuse · Submit a Tip"
        description="Submit a tip or upload video evidence of animal abuse. Reports are routed to the nearest law enforcement or animal control agency."
        path="/report"
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="label-eyebrow">Section · 02 · Report</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Report animal abuse.
          </h1>
          <p className="text-zinc-700 mt-4 max-w-xl leading-relaxed">
            Your report goes to our intake queue and can be routed to the
            nearest law enforcement or animal control agency. Upload video
            evidence if you have it.
          </p>

          <div className="mt-6 border border-red-600 bg-red-50 p-4 flex items-start gap-3">
            <AlertTriangle className="text-red-700 mt-0.5" size={18} />
            <div className="text-sm text-red-900">
              <strong className="uppercase tracking-widest text-[11px] font-mono block mb-1">
                Emergency?
              </strong>
              If an animal is in immediate danger, call <strong>911</strong>{" "}
              now. Do not confront the abuser.
            </div>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="report-form">
            <div>
              <label className="label-eyebrow block mb-2">Incident Type *</label>
              <select
                value={form.incident_type}
                onChange={(e) => set("incident_type", e.target.value)}
                className="field"
                data-testid="report-incident-type"
              >
                {INCIDENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">
                Describe what you witnessed *
              </label>
              <textarea
                value={form.incident_description}
                onChange={(e) => set("incident_description", e.target.value)}
                rows={5}
                className="field"
                placeholder="Be specific: date, time, animals involved, visible conditions..."
                data-testid="report-description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="label-eyebrow block mb-2">
                  Incident Location (address or landmark) *
                </label>
                <input
                  value={form.location_text}
                  onChange={(e) => set("location_text", e.target.value)}
                  className="field"
                  placeholder="123 Main St, or nearest intersection"
                  data-testid="report-location"
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">City</label>
                <input
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  className="field"
                  data-testid="report-city"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label-eyebrow block mb-2">State</label>
                <input
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  className="field"
                  placeholder="e.g. Texas"
                  data-testid="report-state"
                />
              </div>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Video Evidence (optional)</label>
              <VideoUploader video={video} setVideo={setVideo} />
            </div>

            <div className="border border-zinc-300 p-5 bg-zinc-50">
              <div className="label-eyebrow mb-3">Your contact (optional — kept confidential)</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  className="field"
                  value={form.reporter_name}
                  onChange={(e) => set("reporter_name", e.target.value)}
                  placeholder="Name"
                  data-testid="report-reporter-name"
                />
                <input
                  className="field"
                  value={form.reporter_email}
                  onChange={(e) => set("reporter_email", e.target.value)}
                  placeholder="Email"
                  type="email"
                  data-testid="report-reporter-email"
                />
                <input
                  className="field"
                  value={form.reporter_phone}
                  onChange={(e) => set("reporter_phone", e.target.value)}
                  placeholder="Phone"
                  data-testid="report-reporter-phone"
                />
              </div>
            </div>

            {uploading && progress > 0 && (
              <div data-testid="upload-progress">
                <div className="label-eyebrow mb-2">Uploading... {progress}%</div>
                <div className="h-2 bg-zinc-200 border border-zinc-300">
                  <div
                    className="h-full bg-red-600 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-4 pt-2">
              <button
                type="submit"
                disabled={uploading}
                className="btn-danger disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="report-submit"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <FileWarning size={14} />}
                {uploading ? "Submitting..." : "Submit Report"}
              </button>
              <Link to="/nearby" className="btn-outline" data-testid="report-connect-station">
                <MapPin size={14} /> Connect to Station Instead
              </Link>
            </div>
          </form>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 space-y-6">
            <div className="border border-zinc-900">
              <img
                src={EVIDENCE_IMG}
                alt="Law enforcement response"
                className="w-full h-64 object-cover grayscale contrast-125"
              />
              <div className="p-5 border-t border-zinc-900">
                <div className="label-eyebrow">Evidence matters</div>
                <div className="font-display font-bold text-lg mt-2 leading-snug">
                  Video and witness testimony build stronger cases in court.
                </div>
              </div>
            </div>

            <div className="border border-zinc-300 p-5 bg-white">
              <div className="label-eyebrow mb-3">Good report checklist</div>
              <ul className="space-y-2 text-sm">
                {[
                  "Date, time and exact location",
                  "Number and type of animals involved",
                  "Visible injuries or conditions",
                  "Names or descriptions of people involved",
                  "Any photo or video evidence",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function VideoUploader({ video, setVideo }) {
  return (
    <div className="border-2 border-dashed border-zinc-400 p-6 bg-white hover:border-zinc-900 transition-colors">
      {!video ? (
        <label
          htmlFor="video-file"
          className="block cursor-pointer text-center"
          data-testid="video-upload-label"
        >
          <Upload className="mx-auto text-zinc-600" size={28} />
          <div className="mt-3 font-display font-bold">
            Drop video here or click to browse
          </div>
          <div className="text-xs text-zinc-500 mt-1 font-mono uppercase tracking-widest">
            MP4, MOV, WEBM — max 100MB
          </div>
          <input
            id="video-file"
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => setVideo(e.target.files?.[0] || null)}
            data-testid="video-file-input"
          />
        </label>
      ) : (
        <div className="flex items-center gap-4" data-testid="video-selected">
          <div className="w-12 h-12 bg-zinc-900 text-white flex items-center justify-center">
            <Video size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold truncate">{video.name}</div>
            <div className="text-xs text-zinc-500 font-mono">
              {(video.size / (1024 * 1024)).toFixed(1)} MB
            </div>
          </div>
          <button
            type="button"
            onClick={() => setVideo(null)}
            className="text-xs uppercase tracking-widest text-red-600 font-semibold hover:underline"
            data-testid="video-remove"
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
